import { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Input, InputNumber, Select, Space, Typography, Divider, Slider, Modal } from 'antd';
import {
  PlayCircleOutlined, PauseCircleOutlined, StopOutlined,
  PlusOutlined, DeleteOutlined, CopyOutlined,
  LockOutlined, UnlockOutlined,
} from '@ant-design/icons';
import { KeyframeEngine, createDefaultClip, createTrack, createKeyframe } from '../../core/KeyframeEngine';
import type { AnimationClip, AnimationTrack, AnimationState, InterpolationType } from '../../core/KeyframeEngine';
import { animationPresets } from '../../core/AnimationPresets';

const { Text } = Typography;

interface TimelinePanelProps {
  engine: KeyframeEngine;
  selectedNodeId: string | null;
  onApplyValues?: (targetId: string, property: string, value: number[]) => void;
}

export default function TimelinePanel({ engine, selectedNodeId, onApplyValues }: TimelinePanelProps) {
  const [state, setState] = useState<AnimationState>(engine.getState());
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [clipName, setClipName] = useState('');
  const [clipDuration, setClipDuration] = useState(5);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = engine.subscribe(setState);
    return unsub;
  }, [engine]);

  // Apply animation values to scene
  useEffect(() => {
    if (!state.isPlaying || !onApplyValues) return;
    const values = engine.evaluateAll();
    values.forEach((props, targetId) => {
      props.forEach((value, property) => {
        onApplyValues(targetId, property, value);
      });
    });
  }, [state.currentTime, state.isPlaying, engine, onApplyValues]);

  const clip = state.clip;
  const progress = state.progress;

  const handleCreateClip = useCallback(() => {
    const newClip = createDefaultClip(clipName || '新动画', clipDuration);
    engine.loadClip(newClip);
    setClipName('');
    setShowPresetModal(false);
  }, [engine, clipName, clipDuration]);

  const handleLoadPreset = useCallback((presetName: string) => {
    const preset = animationPresets.find(p => p.name === presetName);
    if (!preset || !selectedNodeId) return;
    const clip = preset.create(selectedNodeId);
    engine.loadClip(clip);
    setShowPresetModal(false);
  }, [engine, selectedNodeId]);

  const handleAddTrack = useCallback(() => {
    if (!clip || !selectedNodeId) return;
    const track = createTrack(
      `属性 ${clip.tracks.length + 1}`,
      selectedNodeId,
      'position.y',
      []
    );
    engine.addTrack(track);
  }, [engine, clip, selectedNodeId]);

  const handleAddKeyframe = useCallback(() => {
    if (!clip || !selectedTrackId) return;
    const track = clip.tracks.find(t => t.id === selectedTrackId);
    if (!track) return;
    const kf = createKeyframe(progress, [0]);
    engine.addKeyframe(selectedTrackId, kf);
  }, [engine, clip, selectedTrackId, progress]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${m}:${s.toString().padStart(2, '0')}.${ms}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1a1a2e' }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #30475e' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: 13, color: '#e0e0e0' }}>时间轴</Text>
          <Space size={4}>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowPresetModal(true)}
              style={{ fontSize: 11 }}
            >
              新建
            </Button>
            {clip && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddTrack}
                disabled={!selectedNodeId}
                style={{ fontSize: 11 }}
              >
                轨道
              </Button>
            )}
          </Space>
        </div>

        {clip && (
          <div style={{ marginTop: 6 }}>
            <Input
              size="small"
              value={clip.name}
              onChange={(e) => { clip.name = e.target.value; }}
              style={{ fontSize: 11, marginBottom: 4 }}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Text style={{ fontSize: 10, color: '#888' }}>时长:</Text>
              <InputNumber
                size="small"
                value={clip.duration}
                min={0.5}
                max={120}
                step={0.5}
                onChange={(v) => { if (v) { clip.duration = v; engine.seek(state.currentTime); } }}
                style={{ width: 60, fontSize: 11 }}
              />
              <Text style={{ fontSize: 10, color: '#888' }}>秒</Text>
              <div style={{ flex: 1 }} />
              <Text style={{ fontSize: 10, color: '#888' }}>循环:</Text>
              <Button
                size="small"
                type={clip.loop ? 'primary' : 'default'}
                onClick={() => { clip.loop = !clip.loop; }}
                style={{ fontSize: 10 }}
              >
                {clip.loop ? '开' : '关'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Transport Controls */}
      {clip && (
        <div style={{ padding: '6px 12px', borderBottom: '1px solid #30475e', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            size="small"
            type={state.isPlaying ? 'primary' : 'default'}
            icon={state.isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => state.isPlaying ? engine.pause() : engine.play()}
          />
          <Button size="small" icon={<StopOutlined />} onClick={() => engine.stop()} />
          <Text style={{ fontSize: 11, color: '#999', minWidth: 60 }}>
            {formatTime(state.currentTime)} / {formatTime(clip.duration)}
          </Text>
          <div style={{ flex: 1 }}>
            <Slider
              min={0}
              max={1}
              step={0.001}
              value={progress}
              onChange={(v) => engine.seekProgress(v)}
              tooltip={{ formatter: (v) => formatTime((v || 0) * clip.duration) }}
              style={{ margin: 0 }}
            />
          </div>
          <Text style={{ fontSize: 10, color: '#888' }}>{clip.tracks.length} 轨道</Text>
        </div>
      )}

      {/* Track List */}
      {clip && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          {clip.tracks.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <Text style={{ fontSize: 12, color: '#666' }}>
                {selectedNodeId ? '点击"轨道"添加动画轨道' : '选中模型后可添加动画'}
              </Text>
            </div>
          ) : (
            clip.tracks.map((track) => (
              <div
                key={track.id}
                onClick={() => setSelectedTrackId(track.id)}
                style={{
                  padding: '6px 12px',
                  borderBottom: '1px solid #2a2a3c',
                  background: selectedTrackId === track.id ? '#2a3a5c' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Button
                    size="small"
                    type="text"
                    icon={track.enabled ? <LockOutlined /> : <UnlockOutlined />}
                    onClick={(e) => { e.stopPropagation(); track.enabled = !track.enabled; }}
                    style={{ color: track.enabled ? '#1890ff' : '#666', fontSize: 10 }}
                  />
                  <Text style={{ fontSize: 11, color: '#e0e0e0', flex: 1 }}>{track.name}</Text>
                  <Text style={{ fontSize: 9, color: '#666' }}>{track.property}</Text>
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => { e.stopPropagation(); engine.removeTrack(track.id); }}
                    style={{ fontSize: 10 }}
                  />
                </div>

                {/* Keyframe dots */}
                <div style={{ position: 'relative', height: 16, marginTop: 4 }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, top: 7, height: 2, background: '#30475e' }} />
                  {track.keyframes.map((kf) => (
                    <div
                      key={kf.id}
                      style={{
                        position: 'absolute',
                        left: `${kf.time * 100}%`,
                        top: 3,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#1890ff',
                        border: '1px solid #40a9ff',
                        transform: 'translateX(-50%)',
                        cursor: 'pointer',
                      }}
                      title={`${kf.time.toFixed(2)} - [${kf.value.join(', ')}]`}
                    />
                  ))}
                </div>

                {/* Keyframes list */}
                {selectedTrackId === track.id && (
                  <div style={{ marginTop: 6 }}>
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={(e) => { e.stopPropagation(); handleAddKeyframe(); }}
                      style={{ fontSize: 10, marginBottom: 4 }}
                    >
                      添加关键帧
                    </Button>
                    {track.keyframes.map((kf) => (
                      <div key={kf.id} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 2 }}>
                        <InputNumber
                          size="small"
                          value={kf.time}
                          min={0}
                          max={1}
                          step={0.05}
                          onChange={(v) => { if (v !== null) engine.updateKeyframe(track.id, kf.id, { time: v }); }}
                          style={{ width: 50, fontSize: 10 }}
                          formatter={(v) => `${(v || 0).toFixed(2)}`}
                        />
                        <Select
                          size="small"
                          value={kf.interpolation}
                          onChange={(v) => engine.updateKeyframe(track.id, kf.id, { interpolation: v as InterpolationType })}
                          style={{ width: 70 }}
                          options={[
                            { value: 'linear', label: '线性' },
                            { value: 'easeIn', label: '缓入' },
                            { value: 'easeOut', label: '缓出' },
                            { value: 'easeInOut', label: '缓入缓出' },
                            { value: 'step', label: '阶梯' },
                          ]}
                        />
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => engine.removeKeyframe(track.id, kf.id)}
                          style={{ fontSize: 10 }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Empty State */}
      {!clip && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 8 }}>
              暂无动画剪辑
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowPresetModal(true)}
            >
              创建动画
            </Button>
          </div>
        </div>
      )}

      {/* Preset Modal */}
      <Modal
        title="创建动画"
        open={showPresetModal}
        onCancel={() => setShowPresetModal(false)}
        footer={null}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 12 }}>空白剪辑</Text>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Input
              size="small"
              placeholder="动画名称"
              value={clipName}
              onChange={(e) => setClipName(e.target.value)}
              style={{ flex: 1 }}
            />
            <InputNumber
              size="small"
              value={clipDuration}
              min={0.5}
              max={120}
              step={0.5}
              onChange={(v) => v && setClipDuration(v)}
              style={{ width: 70 }}
              addonAfter="秒"
            />
            <Button size="small" type="primary" onClick={handleCreateClip}>
              创建
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Text strong style={{ fontSize: 12 }}>预设动画</Text>
        <div style={{ marginTop: 8, maxHeight: 300, overflow: 'auto' }}>
          {animationPresets.map((preset) => (
            <div
              key={preset.name}
              onClick={() => handleLoadPreset(preset.name)}
              style={{
                padding: '8px 12px',
                border: '1px solid #30475e',
                borderRadius: 4,
                marginBottom: 6,
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1890ff')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#30475e')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 12, color: '#e0e0e0' }}>{preset.name}</Text>
                <Text style={{ fontSize: 10, color: '#888' }}>{preset.duration}s</Text>
              </div>
              <Text style={{ fontSize: 10, color: '#666' }}>{preset.description}</Text>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
